import { useState, useEffect, useCallback } from "react";
import { Bell, Check, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { ScrollArea } from "./ui/scroll-area";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";

export default function NotificationDropdown() {
  const nav = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        apiClient.get("/notifications?limit=20"),
        apiClient.get("/notifications/unread-count")
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (e) {
      console.error("Failed to fetch notifications", e);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (mounted) await fetchNotifications();
    };
    load();
    const interval = setInterval(fetchNotifications, 60000);
    return () => { 
      mounted = false;
      clearInterval(interval); 
    };
  }, [fetchNotifications]);

  const markAsRead = async (notifId) => {
    try {
      await apiClient.patch(`/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notifId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error("Failed to mark as read", e);
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await apiClient.post("/notifications/mark-all-read");
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (e) {
      toast.error("Failed to mark all as read");
    }
    setLoading(false);
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.id);
    if (notif.deep_link) nav(notif.deep_link);
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px] bg-red-500">
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-white dark:bg-gray-950" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <p className="font-semibold text-sm">Notifications</p>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} disabled={loading} className="text-xs h-7">
              <Check className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-72">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              No notifications yet
            </div>
          ) : (
            notifications.map((notif) => (
              <DropdownMenuItem
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer ${!notif.is_read ? "bg-primary/5" : ""}`}
              >
                <div className="flex items-start justify-between w-full gap-2">
                  <p className={`text-sm ${!notif.is_read ? "font-medium" : ""}`}>{notif.title}</p>
                  {!notif.is_read && <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{notif.message}</p>
                <div className="flex items-center justify-between w-full mt-1">
                  <span className="text-[10px] text-muted-foreground">{formatTime(notif.created_at)}</span>
                  {notif.deep_link && <ExternalLink className="w-3 h-3 text-muted-foreground" />}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        <DropdownMenuSeparator />
        <div className="p-2">
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={fetchNotifications}>
            Refresh
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
